import { validate as validateAddress } from '@dartess/multicoin-address-validator';
import { validate } from 'crypto-address-validators';
import moment from 'moment';
import validator from 'validator';
import { CountryTypes } from '@/types/country';
import { TransactTypes } from '@/types/transact';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
var CountryCodes = CountryTypes.CountryCodes;
var Countries = CountryTypes.Countries;
var ChannelTypes = WidgetApiServiceTypes.ChannelTypes;
export const getTransactionChannelType = (transaction) => {
    const accountType = transaction.bankInfo?.paymentLink
        ? 'eft'
        : transaction.source?.accountType.toLowerCase();
    return Object.values(ChannelTypes).find(value => value.toLowerCase() === accountType);
};
export const formatDate = (d, format = 'LLL') => moment(d).format(format);
export const numberFormat = (number, maximumFractionDigits = 2, minimumFractionDigits = 0) => {
    if (!number) {
        return '0.00';
    }
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(number);
};
export const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const seconds = time % 60;
    if (hours > 0) {
        const minutes = Math.floor((time % 3600) / 60);
        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}s`;
    }
    const minutes = Math.floor(time / 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}s`;
};
export const titleCase = (str) => {
    if (!str)
        return str;
    const words = str.toLowerCase().split(' ');
    return words
        .map(word => word[0].toUpperCase().concat(word.slice(1)))
        .join(' ');
};
export function getChannelLabel(channelType) {
    switch (channelType) {
        case WidgetApiServiceTypes.ChannelTypes.BANK:
        case WidgetApiServiceTypes.ChannelTypes.EFT:
        case WidgetApiServiceTypes.ChannelTypes.P2P:
            return 'Bank';
        case WidgetApiServiceTypes.ChannelTypes.MOMO:
            return 'Mobile Money';
        default:
            return '';
    }
}
export function getChannelType(channelType) {
    switch (channelType) {
        case WidgetApiServiceTypes.ChannelTypes.BANK:
        case WidgetApiServiceTypes.ChannelTypes.EFT:
        case WidgetApiServiceTypes.ChannelTypes.P2P:
            return 'bank';
        case WidgetApiServiceTypes.ChannelTypes.MOMO:
            return 'momo';
        default:
            return '';
    }
}
export function getCountryCodeFromCountry(countryName) {
    const countryKey = Object.keys(Countries).find(key => Countries[key] === countryName);
    if (countryKey) {
        return CountryCodes[countryKey];
    }
    return undefined;
}
export function streetIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z0-9_\-\s.,:;@!]+$/;
    return !legalCharactersPattern.test(input);
}
export function nameIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z\-\s]+$/;
    return !legalCharactersPattern.test(input);
}
export function postalCodeIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z0-9_\-\s:]+$/;
    return !legalCharactersPattern.test(input);
}
export function IdIsInvalid(input, regex, flag) {
    if (!regex)
        return false;
    const expression = new RegExp(regex, flag);
    return !expression.test(input);
}
export function validateMobileNumber(value, minLength, maxLength) {
    // TODO this needs to be relooked at
    const MIN_PHONE_LENGTH = minLength ?? 7;
    const MAX_PHONE_LENGTH = maxLength ?? 10;
    if (!/^\d+$/.test(value))
        return ' ';
    if (value.startsWith('0') && value.length < MAX_PHONE_LENGTH) {
        return ' ';
    }
    if (value.length < MIN_PHONE_LENGTH || value.length > MAX_PHONE_LENGTH) {
        return ' ';
    }
    return true;
}
export const isValidEmail = (email) => validator.isEmail(email);
export const maskString = (str, maskChar = '*', totalLength = 8, visibleChars = 4) => {
    if (str.length >= totalLength) {
        return (maskChar.repeat(totalLength - visibleChars) + str.slice(-visibleChars));
    }
    const padding = totalLength - str.length;
    return maskChar.repeat(padding) + str;
};
export const addCountryPrefix = (phoneNumber, countryCode) => {
    if (phoneNumber.startsWith(countryCode)) {
        return phoneNumber;
    }
    return `${countryCode}${phoneNumber}`;
};
export const removeCountryPrefix = (phoneNumber, countryCode) => {
    if (phoneNumber.startsWith(countryCode)) {
        return phoneNumber.slice(countryCode.length);
    }
    return phoneNumber;
};
export const validateAddressWithRegex = (address, regex) => regex.test(address);
export function isValidWalletAddress(wallet) {
    const { address, networkType, standard, token } = wallet;
    const normalizedStandard = standard?.toUpperCase();
    const { ERC20, TRC20 } = TransactTypes.Network;
    // Helper function to check if standard is ERC20-compatible
    const isERC20Compatible = (std) => ['CELO', 'ERC20', 'POLYGON'].includes(std || '');
    switch (token) {
        case 'USDT':
            if (isERC20Compatible(normalizedStandard)) {
                return validate(address, 'ETH', ERC20);
            }
            if (normalizedStandard === 'TON') {
                // Support TON addresses with optional memo tags: UQxxxxx_memo
                // Memo uses same base64url character set as address: A-Za-z0-9_-
                return validateAddressWithRegex(address, /^(EQ|UQ)[A-Za-z0-9_-]{46}(_[A-Za-z0-9_-]+)?$/);
            }
            if (normalizedStandard === 'SOL') {
                return validateAddress(address, 'solana');
            }
            return (validate(address, token, TRC20) &&
                validateAddressWithRegex(address, /T[A-Za-z1-9]{33}/));
        case 'ETH':
            return validate(address, token, ERC20);
        case 'PYUSD':
            return validate(address, 'eth');
        case 'XAUT':
            switch (networkType) {
                case ERC20:
                    return validate(address, 'eth');
                case TRC20:
                    return validate(address, 'tron');
                default:
                    return false;
            }
        case 'SOL':
            return validateAddress(address, 'solana');
        case 'ADA':
            return validateAddress(address, 'ada');
        case 'MATIC':
            return validateAddress(address, 'matic');
        case 'BTC':
            return validate(address, token);
        case 'USDC':
            if (normalizedStandard === 'SOL') {
                return validateAddress(address, 'solana');
            }
            if (isERC20Compatible(normalizedStandard)) {
                return validate(address, 'ETH', ERC20);
            }
            if (networkType === 'XLM') {
                // Support different Stellar memo types:
                // _123456789 (MEMO_ID - numeric)
                // _text (MEMO_TEXT - up to 28 chars)
                // _abcdef123... (MEMO_HASH/MEMO_RETURN - hex)
                return validateAddressWithRegex(address, /^G[A-Z2-7]{55}(_[a-zA-Z0-9]{1,64})?$/);
            }
            return validateAddress(address, token);
        case 'CUSD':
            if (isERC20Compatible(normalizedStandard)) {
                return validate(address, 'ETH', ERC20);
            }
            return validateAddress(address, token);
        case 'TON':
            // Support TON addresses with optional memo tags: UQxxxxx_memo
            // Memo uses same base64url character set as address: A-Za-z0-9_-
            return validateAddressWithRegex(address, /^(EQ|UQ)[A-Za-z0-9_-]{46}(_[A-Za-z0-9_-]+)?$/);
        case 'RLUSD':
            return validateAddressWithRegex(address, /^r[0-9a-zA-Z]{24,34}$/);
        case 'XLM':
            // Support different Stellar memo types:
            // _123456789 (MEMO_ID - numeric)
            // _text (MEMO_TEXT - up to 28 chars)
            // _abcdef123... (MEMO_HASH/MEMO_RETURN - hex)
            return validateAddressWithRegex(address, /^G[A-Z2-7]{55}(_[a-zA-Z0-9]{1,64})?$/);
        default:
            return false;
    }
}
export const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
export function validateUrl(url) {
    if (!url)
        return false;
    try {
        const parsedURL = new URL(url);
        const allowedProtocols = ['https:'];
        if (!allowedProtocols.includes(parsedURL.protocol.toLowerCase())) {
            return false;
        }
        if (!parsedURL.hostname) {
            return false;
        }
        if (/javascript:/i.test(url)) {
            return false;
        }
        const securityChecks = {
            noDataProtocol: !url.toLowerCase().includes('data:'),
            noVBScriptProtocol: !url.toLowerCase().includes('vbscript:'),
            noFileProtocol: !url.toLowerCase().includes('file:'),
            noScriptTags: !/<script.*?>.*?<\/script>/i.test(url),
            noOnHandlers: !/(on\w+\s*=)/i.test(url),
            noSuspiciousEncoding: !/(%0[0-9A-Fa-f]|%1[0-9A-Fa-f]|%2[0-9A-Fa-f]|%3[0-9A-Fa-f])/i.test(url),
        };
        if (Object.values(securityChecks).includes(false)) {
            return false;
        }
        return true;
    }
    catch (error) {
        return false;
    }
}
